import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Package,
  Edit,
  Truck,
  Phone,
  MapPin,
  Mail,
  Image as ImageIcon,
  Camera,
} from 'lucide-react';
import { useEnvio, useUpdateEnvio } from '../../hooks/useEnvios';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import { Envio } from '@/types/envio';

interface EnvioWithOptional extends Envio {
  clienteEmail?: string | null;
}

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    pendiente: { variant: 'warning', label: 'Pendiente' },
    en_proceso: { variant: 'info', label: 'En Proceso' },
    entregado: { variant: 'success', label: 'Entregado' },
    cancelado: { variant: 'danger', label: 'Cancelado' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const getEstadoIcon = (estado: string) => {
  switch (estado) {
    case 'pendiente': return <Clock size={16} className="text-yellow-500" />;
    case 'en_proceso': return <Truck size={16} className="text-blue-500" />;
    case 'entregado': return <CheckCircle size={16} className="text-green-500" />;
    case 'cancelado': return <XCircle size={16} className="text-red-500" />;
    default: return null;
  }
};

export function EnvioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const envioId = id ? parseInt(id) : 0;

  const { data: envioRaw, isLoading, refetch } = useEnvio(envioId);
  const envio = envioRaw as EnvioWithOptional | undefined;
  const updateEnvio = useUpdateEnvio();

  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  const handleChangeEstado = async (nuevoEstado: string) => {
    if (!confirm(`¿Cambiar estado a "${nuevoEstado}"?`)) return;

    setCambiandoEstado(true);
    try {
      await updateEnvio.mutateAsync({
        id: envioId,
        data: { estado: nuevoEstado },
      });
      toast.success(`Estado actualizado a "${nuevoEstado}"`);
      refetch();
    } catch (error) {
    } finally {
      setCambiandoEstado(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando envío...</div>
      </div>
    );
  }

  if (!envio) {
    return (
      <div className="text-center py-8 text-red-500">
        Envío no encontrado
      </div>
    );
  }

  const productos = JSON.parse(envio.productos || '[]');
  const total = productos.reduce((sum: number, p: any) => sum + (p.precio * p.cantidad), 0);

  const puedeEditar = envio.estado === 'pendiente';
  const puedeCambiarEstado = envio.estado !== 'cancelado' && envio.estado !== 'entregado';

  const tieneImagenObservacion = envio.imagenObservacionUrl && envio.imagenObservacionUrl.trim() !== '';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/envios')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-800">
                {envio.numeroEnvio}
              </h1>
              {getEstadoBadge(envio.estado)}
            </div>
            <p className="text-sm text-gray-500">
              {envio.clienteNombre} • {formatDate(envio.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {puedeEditar && (
            <Button
              variant="outline"
              onClick={() => navigate(`/envios/editar/${envio.id}`)}
            >
              <Edit size={16} className="mr-2" />
              Editar
            </Button>
          )}
          {puedeCambiarEstado && (
            <>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => handleChangeEstado('en_proceso')}
                disabled={cambiandoEstado}
              >
                <Truck size={16} className="mr-2" />
                En Proceso
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleChangeEstado('entregado')}
                disabled={cambiandoEstado}
              >
                <CheckCircle size={16} className="mr-2" />
                Entregar
              </Button>
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => handleChangeEstado('cancelado')}
                disabled={cambiandoEstado}
              >
                <XCircle size={16} className="mr-2" />
                Cancelar
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Users size={20} className="text-blue-500" />
              Datos del Cliente
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-medium text-gray-800">{envio.clienteNombre}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Dirección</p>
                <a
                  href={`https://www.google.com/maps/search/${encodeURIComponent(envio.clienteDireccion)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors flex items-center gap-1"
                >
                  <MapPin size={14} className="text-blue-500 flex-shrink-0" />
                  <span className="truncate">{envio.clienteDireccion}</span>
                </a>
              </div>
              
              {envio.clienteTelefono && (
                <div>
                  <p className="text-sm text-gray-500">Teléfono</p>
                  <a
                    href={`tel:${envio.clienteTelefono.replace(/\s/g, '')}`}
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors flex items-center gap-1"
                  >
                    <Phone size={14} className="text-blue-500" />
                    {envio.clienteTelefono}
                  </a>
                </div>
              )}
              
              {envio.clienteEmail && (
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <a
                    href={`mailto:${envio.clienteEmail}`}
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors flex items-center gap-1"
                  >
                    <Mail size={14} className="text-blue-500" />
                    {envio.clienteEmail}
                  </a>
                </div>
              )}
            </div>
          </Card>

          {/* Productos */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Package size={20} className="text-blue-500" />
              Productos
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-2">Producto</th>
                    <th className="pb-2 text-center">Cantidad</th>
                    <th className="pb-2 text-right">Precio</th>
                    <th className="pb-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {productos.map((producto: any, index: number) => (
                    <tr key={index} className="text-sm">
                      <td className="py-2 font-medium text-gray-800">{producto.nombre}</td>
                      <td className="py-2 text-center">{producto.cantidad}</td>
                      <td className="py-2 text-right">{formatCurrency(producto.precio)}</td>
                      <td className="py-2 text-right">{formatCurrency(producto.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-bold">
                    <td colSpan={3} className="py-2 text-right">Total</td>
                    <td className="py-2 text-right text-blue-600">{formatCurrency(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        {/* Columna derecha */}
        <div className="space-y-6">
          {/* Detalles del envío */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Detalles del Envío</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Estado</p>
                <p className="font-medium text-gray-800 flex items-center gap-2">
                  {getEstadoIcon(envio.estado)}
                  {envio.estado}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fecha de Creación</p>
                <p className="font-medium text-gray-800">{formatDate(envio.createdAt)}</p>
              </div>
              {envio.fechaEnvio && (
                <div>
                  <p className="text-sm text-gray-500">Fecha de Envío</p>
                  <p className="font-medium text-gray-800">{formatDate(envio.fechaEnvio)}</p>
                </div>
              )}
              {envio.fechaEntrega && (
                <div>
                  <p className="text-sm text-gray-500">Fecha de Entrega</p>
                  <p className="font-medium text-gray-800">{formatDate(envio.fechaEntrega)}</p>
                </div>
              )}
              {envio.numeroSeguimiento && (
                <div>
                  <p className="text-sm text-gray-500">Número de Seguimiento</p>
                  <p className="font-medium text-gray-800">{envio.numeroSeguimiento}</p>
                </div>
              )}
              {envio.observaciones && (
                <div>
                  <p className="text-sm text-gray-500">Observaciones</p>
                  <p className="text-sm text-gray-600">{envio.observaciones}</p>
                </div>
              )}
              
              {/* Imagen de Evidencia */}
              {tieneImagenObservacion && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <h4 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-2">
                    <Camera size={14} className="text-gray-400" />
                    Foto de evidencia de entrega
                  </h4>
                  <div className="relative inline-block">
                    <img
                      src={envio.imagenObservacionUrl}
                      alt="Evidencia de entrega"
                      className="max-w-xs max-h-48 rounded-lg border border-gray-200 shadow-sm object-contain bg-gray-50"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-image.png';
                      }}
                    />
                    <button
                      onClick={() => {
                        window.open(envio.imagenObservacionUrl, '_blank');
                      }}
                      className="absolute bottom-2 right-2 p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
                      title="Ver imagen completa"
                    >
                      <ImageIcon size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Confirmación por Email */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Confirmación por Email</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Email enviado</span>
                <span className={`font-medium ${envio.emailEnviado ? 'text-green-600' : 'text-gray-400'}`}>
                  {envio.emailEnviado ? ' Sí' : ' No'}
                </span>
              </div>
              {envio.emailFecha && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Fecha de envío</span>
                  <span className="font-medium text-gray-800">{formatDate(envio.emailFecha)}</span>
                </div>
              )}
              {envio.estado === 'entregado' && !envio.emailEnviado && envio.clienteEmail && (
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => {
                    navigate('/social', { 
                      state: { 
                        tab: 'confirmaciones',
                        envioId: envio.id 
                      } 
                    });
                  }}
                >
                  <Mail size={16} className="mr-2" />
                  Enviar Email de Confirmación
                </Button>
              )}
              {envio.estado === 'entregado' && !envio.clienteEmail && (
                <p className="text-xs text-yellow-600 text-center">
                  El cliente no tiene email registrado
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}